import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import StonLogo from "@/components/StonLogo";
import StatusChip from "@/components/StatusChip";
import { formatDate, formatDateTime } from "@/lib/format";
import {
  Package,
  FilePdf,
  ShieldCheck,
  Ruler,
  CheckCircle,
  Circle,
  Clock,
  Truck,
  ImageSquare,
  Certificate,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicTrack() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fileUrl = useCallback(
    (path) => `${BASE}/api/public/track/${orderId}/file/${path}`,
    [orderId]
  );

  const [fileError, setFileError] = useState("");

  // Fetch a file as a blob (matching how images load) and open the object URL.
  // Retries with exponential backoff on a short timeout, and surfaces a clear
  // message if the storage service is unavailable. The tab is opened
  // synchronously inside the click gesture so mobile popup blockers don't kill it.
  const openFile = useCallback(
    async (path) => {
      setFileError("");
      const win = window.open("", "_blank");
      const url = `${BASE}/api/public/track/${orderId}/file/${path}`;
      let delay = 400;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          const res = await axios.get(url, { responseType: "blob", timeout: 15000 });
          const objectUrl = URL.createObjectURL(res.data);
          if (win) win.location = objectUrl;
          else window.location.assign(objectUrl);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
          return;
        } catch (e) {
          if (attempt === 3) {
            if (win) win.close();
            setFileError("Couldn't open the file — the storage service may be busy. Please try again.");
            setTimeout(() => setFileError(""), 6000);
            return;
          }
          await new Promise((r) => setTimeout(r, delay));
          delay *= 2;
        }
      }
    },
    [orderId]
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BASE}/api/public/track/${orderId}`);
        setData(res.data);
      } catch (e) {
        setError(true);
      }
      setLoading(false);
    })();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] p-6">
        <div className="max-w-5xl mx-auto h-96 bg-white border border-zinc-200 animate-pulse" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] grid place-items-center p-6">
        <div
          className="bg-white border border-zinc-200 p-10 text-center max-w-md"
          data-testid="public-track-not-found"
        >
          <div className="font-display text-2xl font-black text-zinc-900">Tracking link not found</div>
          <p className="mt-2 text-sm text-zinc-600">
            This tracking link is invalid or the order no longer exists. Please ask your supplier for an
            updated link.
          </p>
        </div>
      </div>
    );
  }

  const p = data.product;
  const productDocs = (p?.documents || []).filter((d) => d.pdf_path || d.icon_path);
  const orderDocs = data.documents || [];
  const certs = orderDocs.filter(
    (d) => d.kind === "certificate" || d.kind === "test_certificate" || d.kind === "warranty"
  );
  const drawings = orderDocs.filter((d) =>
    ["drawing", "spec_sheet", "tech_datasheet"].includes(d.kind)
  );
  const gallery = (p?.gallery || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="public-track-page">
      {fileError && (
        <div
          data-testid="public-file-error"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-[#B22222] text-white text-sm px-4 py-3 shadow-lg rounded max-w-[92vw]"
        >
          {fileError}
        </div>
      )}
      {/* Header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-5 flex items-center justify-between gap-4">
          <div>
            <StonLogo size={20} subtitle="Building Materials" className="text-white" />
          </div>
          <div className="text-right">
            <div className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">Order</div>
            <div className="font-mono-mt text-sm" data-testid="public-order-number">
              {data.order_number}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {/* TOP — product details */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          <div className="relative aspect-[4/3] bg-zinc-100 border border-zinc-200 overflow-hidden">
            {p?.image_path ? (
              <img
                src={fileUrl(p.image_path)}
                alt={data.product_name}
                data-testid="public-product-image"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center">
                <Package size={64} weight="duotone" className="text-zinc-300" />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-3">
              <StatusChip status={data.current_status} testId="public-status-chip" />
              <span className="mt-tiny text-zinc-400">Live status</span>
            </div>
            <h1
              className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-3 leading-[1.1]"
              data-testid="public-product-name"
            >
              {data.product_name}
            </h1>
            {(p?.brand || data.product_type) && (
              <div className="mt-1 text-sm uppercase tracking-[0.18em] text-[#8B4513]">
                {p?.brand || data.product_type}
              </div>
            )}
            {p?.description && (
              <p className="mt-4 text-sm md:text-base text-zinc-700 leading-relaxed">{p.description}</p>
            )}

            <div className="grid grid-cols-2 gap-3 mt-6" data-testid="public-order-meta">
              <Meta label="Quantity" value={data.quantity} />
              <Meta label="Expected delivery" value={formatDate(data.expected_delivery)} />
              {data.project_name && <Meta label="Project" value={data.project_name} />}
              <Meta label="Supplied by" value="Ston Link" />
            </div>

            {gallery.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2" data-testid="public-product-gallery">
                {gallery.map((g) => (
                  <img
                    key={g.image_id}
                    src={fileUrl(g.image_path)}
                    alt="Product view"
                    className="w-16 h-16 object-cover border border-zinc-200"
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Technical specs */}
        {(Object.keys(data.specifications || {}).length > 0 ||
          (p?.materials || []).length > 0 ||
          (p?.option_sizes || []).length > 0 ||
          (p?.finishes || []).length > 0) && (
          <section className="mt-10" data-testid="public-specs">
            <SectionTitle icon={Ruler} label="Technical specifications" />
            <div className="bg-white border border-zinc-200 p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {Object.entries(data.specifications || {})
                .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== "")
                .map(([k, v]) => (
                  <Spec key={k} label={prettyLabel(k)} value={String(v)} />
                ))}
              {(p?.materials || []).length > 0 && (
                <Spec label="Material" value={p.materials.join(" · ")} />
              )}
              {(p?.option_sizes || []).length > 0 && (
                <Spec label="Sizes" value={p.option_sizes.join(" · ")} />
              )}
              {(p?.finishes || []).length > 0 && (
                <div>
                  <div className="mt-tiny text-zinc-400">Finishes</div>
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    {p.finishes.map((f) => (
                      <span key={f.name} className="flex items-center gap-1.5 text-sm text-zinc-800">
                        <span
                          className="w-5 h-5 rounded-full border border-zinc-200"
                          style={{ backgroundColor: f.hex || "#d4d4d8" }}
                        />
                        {f.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Drawings + certifications */}
        {(productDocs.length > 0 || drawings.length > 0 || certs.length > 0) && (
          <section className="mt-10" data-testid="public-documents">
            <SectionTitle icon={Certificate} label="Drawings & certifications" />
            <div className="bg-white border border-zinc-200 p-6">
              {productDocs.length > 0 && (
                <div
                  className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4"
                  data-testid="public-product-docs"
                >
                  {productDocs
                    .slice()
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((d) => (
                      <button
                        type="button"
                        key={d.doc_id}
                        onClick={() => openFile(d.pdf_path || d.icon_path)}
                        data-testid={`public-doc-${d.doc_id}`}
                        className="group flex flex-col items-center gap-2 text-center hover:-translate-y-0.5 transition-transform"
                      >
                        <div className="w-full aspect-square border border-zinc-200 bg-zinc-50 grid place-items-center overflow-hidden group-hover:border-[#0047AB]">
                          {d.icon_path ? (
                            <img
                              src={fileUrl(d.icon_path)}
                              alt={d.name}
                              className="w-full h-full object-contain p-2"
                            />
                          ) : (
                            <FilePdf size={32} className="text-[#B22222]" weight="duotone" />
                          )}
                        </div>
                        <div className="text-xs font-medium text-zinc-800 leading-tight line-clamp-2">
                          {d.name}
                        </div>
                      </button>
                    ))}
                </div>
              )}

              {(drawings.length > 0 || certs.length > 0) && (
                <div className={`grid gap-2 ${productDocs.length > 0 ? "mt-6 pt-6 border-t border-zinc-200" : ""}`}>
                  {[...drawings, ...certs].map((f) => (
                    <button
                      type="button"
                      key={f.file_id}
                      onClick={() => openFile(f.storage_path)}
                      data-testid={`public-file-${f.file_id}`}
                      className="flex items-center gap-3 p-3 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 transition-colors text-left w-full"
                    >
                      <FilePdf size={20} className="text-[#B22222] shrink-0" weight="duotone" />
                      <span className="text-sm text-zinc-900 truncate">{f.original_filename}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* BOTTOM — live tracking timeline */}
        <section className="mt-12" data-testid="public-timeline-section">
          <SectionTitle icon={Truck} label="Live production & delivery tracking" />
          <div className="bg-white border border-zinc-200 p-6 md:p-8">
            <PublicTimeline
              timeline={data.timeline || []}
              currentStatus={data.current_status}
              fileUrl={fileUrl}
              openFile={openFile}
            />
          </div>
        </section>

        {data.warranty_info && (
          <section className="mt-8 bg-white border border-zinc-200 p-6 flex items-start gap-4">
            <div className="w-11 h-11 bg-[#0047AB] grid place-items-center shrink-0">
              <ShieldCheck size={22} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-zinc-900">Warranty</div>
              <p className="mt-1 text-sm text-zinc-700 leading-relaxed">{data.warranty_info}</p>
            </div>
          </section>
        )}

        <footer className="mt-12 pb-10 text-center">
          <div className="mt-tiny text-zinc-400">
            Shared by Ston Link — Building Materials · Read-only tracking view
          </div>
        </footer>
      </main>
    </div>
  );
}

function prettyLabel(key) {
  const s = String(key).replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function SectionTitle({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="text-[#FF5A1F]" weight="duotone" />
      <h2 className="font-display text-lg md:text-xl font-bold tracking-tight text-zinc-900">{label}</h2>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="p-3 bg-white border border-zinc-200">
      <div className="mt-tiny text-zinc-400">{label}</div>
      <div className="font-display font-bold text-zinc-900 mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="mt-tiny text-zinc-400">{label}</div>
      <div className="mt-1 font-mono-mt text-sm text-zinc-900">{value}</div>
    </div>
  );
}

function PublicTimeline({ timeline, currentStatus, fileUrl, openFile }) {
  const currentIdx = timeline.findIndex((s) => s.key === currentStatus);
  return (
    <div className="relative" data-testid="public-order-timeline">
      {timeline.map((step, i) => {
        const isCurrent = i === currentIdx;
        const isDone = step.completed && !isCurrent;
        const atts = step.attachments || [];
        return (
          <div
            key={step.key}
            className="relative pl-12 pb-8 last:pb-0"
            data-testid={`public-timeline-step-${step.key}`}
          >
            {i < timeline.length - 1 && (
              <div
                className={`absolute left-[15px] top-8 bottom-0 w-[2px] ${
                  step.completed && timeline[i + 1].completed
                    ? "bg-[#0047AB]"
                    : step.completed
                    ? "bg-gradient-to-b from-[#0047AB] to-zinc-200"
                    : "bg-zinc-200"
                }`}
              />
            )}
            <div className="absolute left-0 top-0">
              {isCurrent ? (
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-[#FF5A1F] animate-ping opacity-40" />
                  <div className="relative w-8 h-8 rounded-full bg-[#FF5A1F] grid place-items-center">
                    <Clock size={16} weight="fill" className="text-white" />
                  </div>
                </div>
              ) : isDone ? (
                <div className="w-8 h-8 rounded-full bg-[#0047AB] grid place-items-center">
                  <CheckCircle size={18} weight="fill" className="text-white" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full border-2 border-zinc-200 bg-white grid place-items-center">
                  <Circle size={12} className="text-zinc-300" />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-baseline justify-between gap-3">
                <div
                  className={`font-display text-lg font-bold ${
                    step.completed || isCurrent ? "text-zinc-900" : "text-zinc-400"
                  }`}
                >
                  {step.label}
                </div>
                {step.completed_at && (
                  <div className="mt-tiny text-zinc-500 font-mono-mt">
                    {formatDateTime(step.completed_at)}
                  </div>
                )}
              </div>
              {step.notes && <div className="mt-2 text-sm text-zinc-600 leading-relaxed">{step.notes}</div>}
              {isCurrent && <div className="mt-2 mt-tiny text-[#FF5A1F]">Current stage</div>}

              {step.tracking_details && (
                <div className="mt-3 flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm">
                  <Truck size={16} className="text-[#0047AB]" weight="duotone" />
                  {/^https?:\/\//i.test(step.tracking_details) ? (
                    <a
                      href={step.tracking_details}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#0047AB] hover:underline break-all"
                    >
                      {step.tracking_details}
                    </a>
                  ) : (
                    <span className="text-zinc-800">{step.tracking_details}</span>
                  )}
                </div>
              )}

              {atts.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {atts.map((a) =>
                    (a.content_type || "").startsWith("image/") ? (
                      <a
                        key={a.file_id}
                        href={fileUrl(a.storage_path)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`public-stage-photo-${a.file_id}`}
                        className="w-24 h-24 border border-zinc-200 overflow-hidden bg-zinc-50 block"
                      >
                        <img
                          src={fileUrl(a.storage_path)}
                          alt={a.filename || "Production photo"}
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      </a>
                    ) : (
                      <button
                        type="button"
                        key={a.file_id}
                        onClick={() => openFile(a.storage_path)}
                        data-testid={`public-stage-file-${a.file_id}`}
                        className="flex items-center gap-2 border border-zinc-200 bg-white px-3 py-2 text-sm hover:border-[#0047AB]"
                      >
                        <FilePdf size={18} className="text-[#B22222]" weight="duotone" />
                        <span className="truncate max-w-[220px]">{a.filename || "Document"}</span>
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {timeline.length === 0 && (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <ImageSquare size={16} /> Tracking will appear here once production starts.
        </div>
      )}
    </div>
  );
}
